import { inject, Injectable } from '@angular/core';
import {
    ComparatorOperators,
    DownloadFileResponse,
    DownloadStandardRiskMapGQL,
    GetStandardRiskMapGQL,
    GetStandardRiskMapQuery,
    HandleStandardRiskmapGQL,
    HandleStandardRiskmapMutation,
    OperatorInput,
    OrderTypeInput,
    RiskMapTemplateQueryComplexInput,
    RiskMapTemplateSortInput,
    RiskMapTemplateColumns,
    UpdateAuStandardRiskmapGQL,
    UpdateAuStandardRiskmapMutation,
    UpdateStatusStandardRiskmapGQL,
    UpdateStatusStandardRiskmapMutation,
    UploadExcelStandardRiskmapGQL,
    UploadExcelStandardRiskmapMutation,
    RiskMapTemplateSimpleQueryInput,
    GetStandardRiskmapFiltersGQL,
    GetStandardRiskmapFiltersQuery
} from '../../../graphql/generated';
import { map, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class StandardRiskmapService {

    private readonly GetStandardRiskMapGQL: GetStandardRiskMapGQL = inject(GetStandardRiskMapGQL);
    private readonly HandleStandardRiskMapGQL: HandleStandardRiskmapGQL = inject(HandleStandardRiskmapGQL);
    private readonly UpdateAuStandardRiskmapGQL: UpdateAuStandardRiskmapGQL = inject(UpdateAuStandardRiskmapGQL);
    private readonly UpdateStatusStandardRiskmapGQL: UpdateStatusStandardRiskmapGQL = inject(UpdateStatusStandardRiskmapGQL);
    private readonly DownloadStandardRiskMapGQL: DownloadStandardRiskMapGQL = inject(DownloadStandardRiskMapGQL);
    private readonly UploadExcelStandardRiskmapGQL: UploadExcelStandardRiskmapGQL = inject(UploadExcelStandardRiskmapGQL);
    private readonly GetStandardRiskmapFiltersGQL: GetStandardRiskmapFiltersGQL = inject(GetStandardRiskmapFiltersGQL);

    getStandardRiskMap(
        size: string,
        typeOfBusiness: string,
        valueChain: string,
        year: string,
        parameters: any,
        au: boolean | null
    ): Observable<GetStandardRiskMapQuery['riskMapTemplateByValueChainAndTypeOfBusinessAndSizeAndYear']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.GetStandardRiskMapGQL.fetch({ size, typeOfBusiness, valueChain, year, query: param.query as RiskMapTemplateQueryComplexInput, sort: param.sort as RiskMapTemplateSortInput, au }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapTemplateByValueChainAndTypeOfBusinessAndSizeAndYear))
    }

    handleStandardRiskMapTree(
        valueChain: string,
        typeOfBusiness: string,
        size: string,
        year: string,
        processId: string,
        riskId: string,
        value: boolean,
        filter: boolean
    ): Observable<HandleStandardRiskmapMutation['handleRiskMapTemplate']> {
        return this.HandleStandardRiskMapGQL.mutate({ input: { processId, riskId, size, typeOfBusiness, value, valueChain, year: Number(year) }, filter }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.handleRiskMapTemplate))
    }

    updateAuStandardRiskMap(
        processId: string,
        size: string,
        typeOfBusiness: string,
        valueChain: string,
        year: number
    ): Observable<UpdateAuStandardRiskmapMutation['updateRiskMapTemplateAu']> {
        return this.UpdateAuStandardRiskmapGQL.mutate({ processId, size, typeOfBusiness, valueChain, year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateRiskMapTemplateAu))
    }

    updateStatusStandardRiskMap(
        processId: string,
        size: string,
        status: boolean,
        typeOfBusiness: string,
        valueChain: string,
        year: number
    ): Observable<UpdateStatusStandardRiskmapMutation['updateRiskMapTemplateStatusTree']> {
        return this.UpdateStatusStandardRiskmapGQL.mutate({ processId, size, typeOfBusiness, valueChain, year, status }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateRiskMapTemplateStatusTree))
    }

    downloadStandardRiskMap(
        parameters: any,
        timeZone: string,
        year: number,
        valueChain: string,
        typeOfBusiness: string,
        size: string
    ): Observable<DownloadFileResponse[]> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.DownloadStandardRiskMapGQL.mutate({ query: param.query as RiskMapTemplateQueryComplexInput, sort: param.sort as RiskMapTemplateSortInput, timeZone, year, size, typeOfBusiness, valueChain }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadRiskMapTemplatesByQuery?.data as DownloadFileResponse[]))
    }

    uploadExcel(
        base64ExcelFile: string
    ): Observable<UploadExcelStandardRiskmapMutation['importRiskMapTemplateFromExcel']> {
        return this.UploadExcelStandardRiskmapGQL.mutate({ base64ExcelFile }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.importRiskMapTemplateFromExcel))
    }

    getFilters(year: string, valueChain: string, typeOfBusiness: string, size: string, au: boolean | null): Observable<GetStandardRiskmapFiltersQuery['riskMapTemplateFilters']> {
        return this.GetStandardRiskmapFiltersGQL.fetch({ au, size, typeOfBusiness, valueChain, year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapTemplateFilters))
    }

}

export function convertLazyLoadToComplexWithoutPage(parameters: any): { query: RiskMapTemplateQueryComplexInput, sort: RiskMapTemplateSortInput } {
    const query: RiskMapTemplateSimpleQueryInput[] = [];
    const fieldsNotBoolean = ['name', 'code', 'cluster', 'note'];
    if (parameters?.filters) {
        Object.entries(parameters.filters).forEach(([field, values]) => {
            //console.log"value", values);

            if (values === null || !Array.isArray(values) || values.length === 0) return;
            (ENUM_CPT[field as keyof typeof ENUM_CPT] || ENUM_CPT.riskParameterName).forEach(key => {
                if (key === 'RISK_PARAMETER_NAME' && values.length !== 1) return;
                let filteredValue: any[] = [];
                if (fieldsNotBoolean.includes(field) && key !== 'RISK_PARAMETER_NAME') {
                    filteredValue = values;
                } else if (!fieldsNotBoolean.includes(field) && key !== 'RISK_PARAMETER_NAME') {
                    filteredValue = values.length > 1 ? [] : values[0] === 'Active' ? ['true'] : ['false'];
                } else if (key === 'RISK_PARAMETER_NAME') {
                    filteredValue = [field, values[0] === 'Active' ? 'true' : 'false'];
                }
                query.push({
                    field: key as RiskMapTemplateColumns,
                    filters: {
                        operator: ComparatorOperators.Match,
                        value: filteredValue
                    }
                });
            });
        });
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.Or
                }
            ]
        },
        sort: {
            sortField: ENUM_CPT?.[parameters?.order?.sortField as keyof typeof ENUM_CPT]?.[0] as RiskMapTemplateColumns || RiskMapTemplateColumns.Sort,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        }
    }
}

export const ENUM_CPT = {
    name: [RiskMapTemplateColumns.Name],
    code: [RiskMapTemplateColumns.Code],
    cluster: [RiskMapTemplateColumns.Cluster],
    au: [RiskMapTemplateColumns.Au],
    auditableUniverse: [RiskMapTemplateColumns.Au],
    riskParameterName: ['RISK_PARAMETER_NAME'],
    status: [RiskMapTemplateColumns.Status]
}